import i18n from '../i18n';
import { todayIso } from './regionalFormat';

export const PERIOD_DAYS = {
  WEEKLY: 7,
  MONTHLY: 30,
  QUARTERLY: 90,
  SEMI_ANNUAL: 180,
  YEARLY: 365,
};

export const PERIOD_INTERVAL_LABELS = {
  WEEKLY: "Weekly (7 days)",
  MONTHLY: "Monthly (30 days)",
  QUARTERLY: "Quarterly (90 days)",
  SEMI_ANNUAL: "Semi-annual (180 days)",
  YEARLY: "Yearly (365 days)",
};

export function getPeriodIntervalOptions() {
  return Object.keys(PERIOD_INTERVAL_LABELS).map((value) => ({
    value,
    label: formatPeriodInterval(value),
  }));
}

export const CHARGES_MODE_LABELS = {
  PER_SESSION: "Per session (flat)",
  PER_SESSION_HOURLY: "Per session (hourly)",
  PER_PERIOD: "Period plan",
  RECURRING_SESSIONS: "Per session (hourly)",
  MONTHLY: "Period plan",
  YEARLY: "Period plan",
};

function translateOrFallback(key, fallback) {
  if (i18n?.exists?.(key)) return i18n.t(key);
  const translated = i18n?.t?.(key);
  if (translated && translated !== key) return translated;
  return fallback;
}

export function formatChargesMode(mode) {
  if (!mode) return "—";
  const fallback = CHARGES_MODE_LABELS[mode] || String(mode).replace(/_/g, " ");
  return translateOrFallback(`charges.modes.${mode}`, fallback);
}

export function formatPeriodInterval(interval) {
  if (!interval) return "";
  const fallback = PERIOD_INTERVAL_LABELS[interval] || interval;
  return translateOrFallback(`charges.intervals.${interval}`, fallback);
}

export function isPeriodBillingMode(mode) {
  return mode === "PER_PERIOD" || mode === "MONTHLY" || mode === "YEARLY";
}

function isoFromDateParts(year, month, day) {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function addDaysToIso(startIso, days) {
  const [y, m, d] = startIso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return isoFromDateParts(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
}

/** Inclusive end date: start + N days - 1 */
export function computePeriodEndDate(startIso, interval) {
  if (!startIso || !interval) return "";
  const days = PERIOD_DAYS[interval] || PERIOD_DAYS.MONTHLY;
  return addDaysToIso(startIso, days - 1);
}

export function computeRenewStartDate(previousEndIso) {
  const today = todayIso();
  if (!previousEndIso) return today;
  const candidate = addDaysToIso(previousEndIso, 1);
  return candidate < today ? today : candidate;
}

export function resolvePeriodCharge(batch, enrollment) {
  if (enrollment?.overridePeriodCharge != null) {
    return Number(enrollment.overridePeriodCharge);
  }
  if (batch?.periodCharge != null) return Number(batch.periodCharge);
  if (batch?.period_charge != null) return Number(batch.period_charge);
  if (enrollment?.overrideMonthlyCharge != null) return Number(enrollment.overrideMonthlyCharge);
  if (batch?.monthlyCharge != null) return Number(batch.monthlyCharge);
  if (batch?.monthly_charge != null) return Number(batch.monthly_charge);
  if (enrollment?.overrideYearlyCharge != null) return Number(enrollment.overrideYearlyCharge);
  if (batch?.yearlyCharge != null) return Number(batch.yearlyCharge);
  if (batch?.yearly_charge != null) return Number(batch.yearly_charge);
  return 0;
}

/** Build a charge snapshot from enrollment API payload or form state. */
export function enrollmentChargeSnapshot(source) {
  if (!source) return {};
  return {
    overridePeriodCharge: source.overridePeriodCharge ?? null,
    overridePeriodInterval: source.overridePeriodInterval ?? null,
    overrideMonthlyCharge: source.overrideMonthlyCharge ?? null,
    overrideYearlyCharge: source.overrideYearlyCharge ?? null,
  };
}

export function resolvePeriodInterval(batch, enrollment) {
  if (enrollment?.overridePeriodInterval) return enrollment.overridePeriodInterval;
  return batch?.periodInterval ?? batch?.period_interval ?? "MONTHLY";
}

export function periodChargeStatusLabel(status) {
  switch (status) {
    case "PAID":
      return { text: i18n.t('charges.status.PAID'), className: "bg-green-100 text-green-800 border-green-200" };
    case "UNPAID":
      return { text: i18n.t('charges.status.UNPAID'), className: "bg-amber-100 text-amber-900 border-amber-200" };
    case "ESCALATED":
      return { text: i18n.t('charges.status.ESCALATED'), className: "bg-red-100 text-red-800 border-red-200" };
    default:
      return null;
  }
}
